import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Save,
  UserPlus,
  Heart,
  Clock3,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, useAuth } from "../lib/auth";
import { useCapabilities } from "../lib/useCapabilities";
import { resolveLandingRoute } from "../lib/experienceResolver";
import BtcLogo from "../components/BtcLogo";
import AssistanceTransparencyCard from "../components/AssistanceTransparencyCard";

const JOURNEY_TYPES = [
  { v: "patient", l: "Patient", d: "I am living with cancer." },
  { v: "survivor", l: "Survivor", d: "I've finished active treatment." },
  { v: "caregiver", l: "Carer", d: "I'm caring for a loved one." },
];

const JOURNEY_STAGES = {
  patient: [
    { v: "newly_diagnosed", l: "Newly diagnosed" },
    { v: "in_treatment", l: "In treatment" },
    { v: "post_treatment", l: "Just finished treatment" },
    { v: "advanced", l: "Living with advanced cancer" },
  ],
  survivor: [
    { v: "recent", l: "Under 1 year post-treatment" },
    { v: "one_to_five", l: "1 to 5 years post-treatment" },
    { v: "long_term", l: "5+ years post-treatment" },
  ],
  caregiver: [
    { v: "supporting_active", l: "Supporting an active patient" },
    { v: "supporting_survivor", l: "Supporting a survivor" },
    { v: "grieving", l: "Grieving a loved one" },
  ],
};

const SUPPORT_NEEDS = [
  { v: "emotional", l: "Emotional support" },
  { v: "practical", l: "Practical everyday help" },
  { v: "information", l: "Trusted information & guidance" },
  { v: "community", l: "Community & connection" },
  { v: "financial", l: "Financial guidance" },
  { v: "carer_respite", l: "Carer respite" },
];

const AVAILABILITY = [
  { v: "weekday_mornings", l: "Weekday mornings" },
  { v: "weekday_afternoons", l: "Weekday afternoons" },
  { v: "weekday_evenings", l: "Weekday evenings" },
  { v: "weekends", l: "Weekends" },
];

const COMMS = [
  { v: "email", l: "Email" },
  { v: "chat", l: "In-portal chat" },
  { v: "phone", l: "Phone call" },
  { v: "video", l: "Video call" },
];

const emptyIntake = {
  journey_type: null,
  journey_stage: null,
  support_needs: [],
  language: "",
  location: "",
  communication_preference: null,
  availability: [],
  buddy_preferences: { similar_diagnosis: false, similar_age: false, same_language: true },
  consent: false,
};

function toggleIn(list, v) {
  const set = new Set(list || []);
  set.has(v) ? set.delete(v) : set.add(v);
  return Array.from(set);
}

export default function Onboarding() {
  const { user, loading } = useAuth();
  // BTC3 Foundation completion · Gate 3 — participation-driven access
  // gate (see below).  `participations` comes from the Experience
  // Resolver — no new capability model.
  const { participations, ready: participationsReady } = useCapabilities();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyIntake);
  const [existingStatus, setExistingStatus] = useState(null); // e.g., 'draft'|'submitted'|...
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lastSavedRef = useRef("");

  // Load existing intake on mount
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    (async () => {
      try {
        const { data } = await api.get("/intake/me");
        if (data?.intake) {
          setExistingStatus(data.intake.status);
          if (data.intake.status !== "draft") {
            // Already submitted — route through the Experience Resolver
            // so the destination reflects the intake's current
            // lifecycle rather than a hard-coded workspace.
            try {
              const dest = await resolveLandingRoute();
              navigate(dest, { replace: true });
            } catch (_e) {
              // Resolver failure — show the onboarding shell; the
              // dashboard link in the UI provides an out.
            }
            return;
          }
          setForm({
            journey_type: data.intake.journey_type || null,
            journey_stage: data.intake.journey_stage || null,
            support_needs: data.intake.support_needs || [],
            language: data.intake.language || "",
            location: data.intake.location || "",
            communication_preference: data.intake.communication_preference || null,
            availability: data.intake.availability || [],
            buddy_preferences: data.intake.buddy_preferences || emptyIntake.buddy_preferences,
            consent: !!data.intake.consent,
          });
        }
      } catch (_e) { /* leave defaults */ }
      finally {
        setReady(true);
      }
    })();
  }, [user, navigate]);

  // Autosave every 1500ms of quiet
  useEffect(() => {
    if (!ready) return;
    const snapshot = JSON.stringify(form);
    if (snapshot === lastSavedRef.current) return;
    const t = setTimeout(async () => {
      try {
        setSaving(true);
        await api.post("/intake/me", form);
        lastSavedRef.current = snapshot;
      } catch (_e) {
        // Silent — user can still Save & Continue explicitly
      } finally {
        setSaving(false);
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [form, ready]);

  const stages = form.journey_type ? JOURNEY_STAGES[form.journey_type] : [];

  const steps = useMemo(
    () => [
      {
        id: "journey",
        title: "Who are you on this journey?",
        subtitle: "This helps us understand where you're coming from.",
        canNext: !!form.journey_type,
        render: () => (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {JOURNEY_TYPES.map((t) => {
              const selected = form.journey_type === t.v;
              return (
                <button
                  key={t.v}
                  type="button"
                  data-testid={`onb-journey-${t.v}`}
                  onClick={() => setForm({ ...form, journey_type: t.v, journey_stage: null })}
                  className={`text-left p-5 rounded-2xl border-2 transition ${
                    selected
                      ? "border-[#2D9B4E] bg-[#2D9B4E]/5"
                      : "border-gray-200 hover:border-[#2D9B4E]/60"
                  }`}
                >
                  <div className="font-bold text-[#0B132B] text-lg">{t.l}</div>
                  <div className="text-sm text-[#4B5563] mt-1">{t.d}</div>
                </button>
              );
            })}
          </div>
        ),
      },
      {
        id: "stage",
        title: "Where in the journey are you?",
        subtitle: "Pick the option that feels closest right now. You can change it later.",
        canNext: !!form.journey_stage,
        render: () => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {stages.map((s) => {
              const selected = form.journey_stage === s.v;
              return (
                <button
                  key={s.v}
                  type="button"
                  data-testid={`onb-stage-${s.v}`}
                  onClick={() => setForm({ ...form, journey_stage: s.v })}
                  className={`text-left p-4 rounded-xl border-2 transition ${
                    selected
                      ? "border-[#2D9B4E] bg-[#2D9B4E]/5"
                      : "border-gray-200 hover:border-[#2D9B4E]/60"
                  }`}
                >
                  <div className="font-semibold text-[#0B132B]">{s.l}</div>
                </button>
              );
            })}
          </div>
        ),
      },
      {
        id: "support",
        title: "What kind of support matters most right now?",
        subtitle: "Pick as many as you'd like — we'll shape your Buddy match around this.",
        canNext: (form.support_needs || []).length > 0,
        render: () => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SUPPORT_NEEDS.map((n) => {
              const active = form.support_needs.includes(n.v);
              return (
                <label
                  key={n.v}
                  data-testid={`onb-need-${n.v}`}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
                    active ? "border-[#2D9B4E] bg-[#2D9B4E]/5" : "border-gray-200 hover:border-[#2D9B4E]/60"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() =>
                      setForm({ ...form, support_needs: toggleIn(form.support_needs, n.v) })
                    }
                    className="w-4 h-4 accent-[#2D9B4E]"
                  />
                  <span className="text-[#0B132B] font-medium">{n.l}</span>
                </label>
              );
            })}
          </div>
        ),
      },
      {
        id: "context",
        title: "Language & where you're based",
        subtitle: "So we can match you with someone nearby or who speaks the same language.",
        canNext: !!(form.language && form.location),
        render: () => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              data-testid="onb-language"
              placeholder="Preferred language (e.g. English)"
              value={form.language || ""}
              onChange={(e) => setForm({ ...form, language: e.target.value })}
              className="px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none"
            />
            <input
              data-testid="onb-location"
              placeholder="City & country (e.g. London, UK)"
              value={form.location || ""}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none"
            />
          </div>
        ),
      },
      {
        id: "comms",
        title: "How would you like your Buddy to reach out first?",
        subtitle: "You can change this at any time.",
        canNext: !!form.communication_preference,
        render: () => (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {COMMS.map((c) => {
              const sel = form.communication_preference === c.v;
              return (
                <button
                  key={c.v}
                  data-testid={`onb-comm-${c.v}`}
                  type="button"
                  onClick={() => setForm({ ...form, communication_preference: c.v })}
                  className={`p-4 rounded-xl border-2 font-semibold text-[#0B132B] transition ${
                    sel ? "border-[#2D9B4E] bg-[#2D9B4E]/5" : "border-gray-200 hover:border-[#2D9B4E]/60"
                  }`}
                >
                  {c.l}
                </button>
              );
            })}
          </div>
        ),
      },
      {
        id: "availability",
        title: "When are you usually free?",
        subtitle: "Pick every window that works for you.",
        canNext: (form.availability || []).length > 0,
        render: () => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {AVAILABILITY.map((a) => {
              const active = (form.availability || []).includes(a.v);
              return (
                <label
                  key={a.v}
                  data-testid={`onb-avail-${a.v}`}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
                    active ? "border-[#2D9B4E] bg-[#2D9B4E]/5" : "border-gray-200 hover:border-[#2D9B4E]/60"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() =>
                      setForm({ ...form, availability: toggleIn(form.availability, a.v) })
                    }
                    className="w-4 h-4 accent-[#2D9B4E]"
                  />
                  <span className="text-[#0B132B] font-medium">{a.l}</span>
                </label>
              );
            })}
          </div>
        ),
      },
      {
        id: "prefs",
        title: "Buddy preferences",
        subtitle: "Optional. These are wishes, not guarantees.",
        canNext: true,
        render: () => (
          <div className="space-y-3">
            {[
              { k: "similar_diagnosis", l: "Prefer someone with a similar diagnosis" },
              { k: "similar_age", l: "Prefer someone around my age" },
              { k: "same_language", l: "Same first language" },
            ].map((p) => (
              <label
                key={p.k}
                className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-[#2D9B4E]/60 cursor-pointer"
              >
                <input
                  data-testid={`onb-pref-${p.k}`}
                  type="checkbox"
                  checked={!!form.buddy_preferences?.[p.k]}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      buddy_preferences: {
                        ...(form.buddy_preferences || {}),
                        [p.k]: e.target.checked,
                      },
                    })
                  }
                  className="w-4 h-4 accent-[#2D9B4E]"
                />
                <span className="text-[#0B132B] font-medium">{p.l}</span>
              </label>
            ))}
          </div>
        ),
      },
      {
        id: "consent",
        title: "One last thing.",
        subtitle: "So we can review your intake and pair you with a Buddy safely.",
        canNext: !!form.consent,
        render: () => (
          <label
            data-testid="onb-consent"
            className="flex items-start gap-3 p-5 rounded-xl border-2 border-gray-200 cursor-pointer hover:border-[#2D9B4E]/60"
          >
            <input
              type="checkbox"
              checked={!!form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-1 w-4 h-4 accent-[#2D9B4E]"
            />
            <span className="text-[#0B132B] leading-relaxed">
              I consent to a BTC Intake Specialist reviewing my responses to pair me with a Buddy.
              I understand my information will be handled in line with the BTC Privacy Policy and
              is never shared publicly.
            </span>
          </label>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, stages]
  );

  const total = steps.length;
  const current = steps[step];
  const pct = Math.round(((step + 1) / total) * 100);

  if (loading || !ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-[#23803F] animate-spin" />
      </div>
    );
  }
  if (!user || typeof user !== "object") return <Navigate to="/login" replace />;
  // BTC3 Foundation completion · Gate 3 — participation-driven intake
  // access.  `participations` is projected by the Experience Resolver
  // and includes "member" for any user with a member_intakes row OR
  // a member role.  Backend intake endpoints reject non-members
  // independently.
  if (participationsReady && !participations.includes("member")) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  const goBack = () => setStep((s) => Math.max(0, s - 1));
  const goNext = async () => {
    // Force a save on step change to be safe
    try {
      await api.post("/intake/me", form);
    } catch (_e) {}
    if (step < total - 1) setStep(step + 1);
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      await api.post("/intake/me", form);
      await api.post("/intake/submit");
      toast.success("Thank you. Your intake is with a Specialist.");
      // Route through the Experience Resolver so the destination
      // reflects the newly-submitted intake's lifecycle.
      try {
        const dest = await resolveLandingRoute();
        navigate(dest, { replace: true });
      } catch (_e) {
        toast.error("Submitted, but we could not open your dashboard. Please retry.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]" data-testid="onboarding-page">
      <div className="max-w-[880px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-14">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <BtcLogo className="h-12 w-auto" />
        </Link>
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-10">
          <AssistanceTransparencyCard variant="onboarding" />
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-4">
            <span>Step {step + 1} of {total}</span>
            <span className="inline-flex items-center gap-1">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {saving ? "Saving..." : "Saved"}
            </span>
          </div>
          <div className="h-1.5 w-full bg-gray-100 rounded-full mb-8 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{ width: `${pct}%`, backgroundColor: "#2D9B4E" }}
            />
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B132B] leading-tight mb-3">
            {current.title}
          </h1>
          <p className="text-[#4B5563] mb-8">{current.subtitle}</p>

          <div className="mb-10">{current.render()}</div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={step === 0}
              data-testid="onb-back"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[#0B132B] font-semibold text-sm hover:bg-[#F8F9FB] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < total - 1 ? (
              <button
                type="button"
                onClick={goNext}
                disabled={!current.canNext}
                data-testid="onb-next"
                className="btc-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={finish}
                disabled={!current.canNext || submitting}
                data-testid="onb-submit"
                className="btc-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Submit for review"}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="mt-6 text-center text-xs text-[#4B5563]">
          You can leave and come back anytime — your answers are saved as you go.
        </div>
      </div>
    </div>
  );
}

// -------- Reusable status card for the dashboard --------
export function IntakeStatusCard() {
  const [intake, setIntake] = useState(undefined);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/intake/me");
        setIntake(data?.eligible ? data.intake : null);
      } catch (_e) {
        setIntake(null);
      }
    })();
  }, []);

  if (intake === undefined) return null;
  if (intake === null) return null;

  const status = intake.public_status;
  const wrap = (node) => (
    <div className="space-y-3">
      <AssistanceTransparencyCard variant="dashboard" />
      {node}
    </div>
  );
  if (status === "in_progress") {
    return wrap(
      <div className="bg-white rounded-2xl border border-[#2D9B4E]/30 p-5 flex items-center justify-between gap-4 shadow-[0_4px_20px_-10px_rgba(45,155,78,0.35)]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#2D9B4E]/10 flex items-center justify-center shrink-0">
            <UserPlus className="w-5 h-5 text-[#23803F]" />
          </div>
          <div>
            <div className="font-bold text-[#0B132B]">Complete your intake to get matched with a Buddy</div>
            <div className="text-sm text-[#4B5563]">Takes about 3 minutes. You can save and resume.</div>
          </div>
        </div>
        <Link to="/onboarding" data-testid="dash-intake-cta" className="btc-btn-primary text-sm shrink-0">
          Continue <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }
  if (status === "under_review") {
    return wrap(
      <div className="bg-white rounded-2xl border border-[#1A5FAB]/30 p-5 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-[#1A5FAB]/10 flex items-center justify-center shrink-0">
          <Clock3 className="w-5 h-5 text-[#1A5FAB]" />
        </div>
        <div>
          <div className="font-bold text-[#0B132B]" data-testid="dash-intake-status">Under review</div>
          <div className="text-sm text-[#4B5563]">A BTC Intake Specialist is reading your responses. We'll be in touch soon.</div>
        </div>
      </div>
    );
  }
  if (status === "complete") {
    return wrap(
      <div className="bg-white rounded-2xl border border-[#2D9B4E]/30 p-5 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-[#2D9B4E]/10 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-5 h-5 text-[#23803F]" />
        </div>
        <div>
          <div className="font-bold text-[#0B132B]" data-testid="dash-intake-status">Intake complete</div>
          <div className="text-sm text-[#4B5563]">Almost there. We're finalising your match now.</div>
        </div>
      </div>
    );
  }
  if (status === "ready_to_meet_buddy") {
    return wrap(
      <div className="bg-white rounded-2xl border border-[#2D9B4E] p-5 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-[#2D9B4E] flex items-center justify-center shrink-0">
          <Heart className="w-5 h-5 text-white" fill="white" />
        </div>
        <div>
          <div className="font-bold text-[#0B132B]" data-testid="dash-intake-status">Ready to meet your Buddy</div>
          <div className="text-sm text-[#4B5563]">You're ready for matching. Watch this space — we'll reach out next.</div>
        </div>
      </div>
    );
  }
  return null;
}

// Named exports for other icons — silence lint on unused
export { MessageCircle, ShieldCheck };
